import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { colors, primaryButtonSx, secondaryButtonSx, typo } from '../../../../theme/tokens';
import { WriteButton } from '../../../../components/ui';
import { RbacRole, RoleInput, rolesV2Api } from '../../../../api/rolesV2Api';
import RolePersonaCard from './RolePersonaCard';
import RolePersonaEditorDialog, { EditorMode } from './RolePersonaEditorDialog';

interface Props {
  onNotify: (msg: string, sev: 'success' | 'error' | 'info') => void;
}

/**
 * Users > Roles tab (RBAC_V2). Lists ROLES (personas) as cards — name,
 * description, permission count, assigned-user count (from the list row's
 * `assigned_user_count`, no per-role fetch). Custom roles can be created /
 * edited / deleted; the two system roles (Default, Super Admin) are locked
 * (view-only).
 *
 * Deletion is GUARDED server-side (decisions §19 — BLOCKED while any user
 * holds the role, never silently orphaned): the confirm dialog re-checks
 * `rolesV2Api.getAssignedUserCount` live (`staleTime: 0`, mirrors
 * `GroupsTable.tsx`'s assigned-count re-check) so blocked/unblocked and the
 * count text key off the current value, not the list row's possibly-stale
 * `assigned_user_count` (used only as the first-paint fallback before the
 * live check resolves); the delete mutation's `onError` still handles a
 * `has_grants` 409 as a backstop in case even the live count raced stale.
 */
const RolePersonasPanel: React.FC<Props> = ({ onNotify }) => {
  const qc = useQueryClient();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>('create');
  const [activeRole, setActiveRole] = useState<RbacRole | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RbacRole | null>(null);

  const rolesQuery = useQuery({
    queryKey: ['rbac-v2', 'roles'],
    queryFn: () => rolesV2Api.listRoles(),
  });
  const roles = rolesQuery.data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['rbac-v2'] });

  const createMut = useMutation({
    mutationFn: (v: RoleInput) => rolesV2Api.createRole(v),
    onSuccess: () => { invalidate(); setEditorOpen(false); onNotify('Role created.', 'success'); },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'role_name_exists') {
        onNotify('A role with that name already exists.', 'error');
      } else if (code === 'role_code_exists') {
        // rolesV2Api slugifies the code from the name — two DIFFERENT names
        // can still collide on the derived code, so this needs its own copy.
        onNotify('A role with a conflicting internal code already exists — try a different name.', 'error');
      } else if (code === 'invalid_role_name' || code === 'invalid_role_code') {
        onNotify('Enter a valid role name.', 'error');
      } else {
        onNotify('Failed to create role.', 'error');
      }
    },
  });

  const updateMut = useMutation({
    mutationFn: (args: { roleCode: string; v: RoleInput }) => rolesV2Api.updateRole(args.roleCode, args.v),
    onSuccess: () => { invalidate(); setEditorOpen(false); setActiveRole(null); onNotify('Role updated.', 'success'); },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'role_name_exists') onNotify('A role with that name already exists.', 'error');
      else if (code === 'invalid_role_name') onNotify('Enter a valid role name.', 'error');
      else onNotify('Failed to update role.', 'error');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (roleCode: string) => rolesV2Api.deleteRole(roleCode),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
      onNotify('Role deleted.', 'success');
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      const count = err?.response?.data?.count ?? deleteTarget?.assigned_user_count ?? 0;
      if (code === 'has_grants') {
        onNotify(`Can’t delete — still held by ${count} user${count === 1 ? '' : 's'}.`, 'error');
      } else {
        onNotify('Failed to delete role.', 'error');
      }
      setDeleteTarget(null);
    },
  });

  const openCreate = () => { setEditorMode('create'); setActiveRole(null); setEditorOpen(true); };
  const openEdit = (r: RbacRole) => { setEditorMode('edit'); setActiveRole(r); setEditorOpen(true); };
  const openView = (r: RbacRole) => { setEditorMode('view'); setActiveRole(r); setEditorOpen(true); };

  const handleSubmit = (v: RoleInput) => {
    if (editorMode === 'edit' && activeRole) updateMut.mutate({ roleCode: activeRole.role_code, v });
    else createMut.mutate(v);
  };

  const saving = createMut.isPending || updateMut.isPending;

  // Live re-check ahead of the delete confirm — mirrors GroupsTable.tsx's
  // assigned-count pattern (staleTime: 0, re-checked every time a target is
  // set so the shown count/blocked-state can't be stale). Falls back to the
  // list row's count for the very first paint (avoids a "0 users" flash
  // before the live query resolves) — self-corrects once it does. The 409
  // has_grants handling in deleteMut.onError stays as a backstop.
  const deleteCountQuery = useQuery({
    queryKey: ['rbac-v2', 'role-assigned-count', deleteTarget?.role_code],
    queryFn: () => rolesV2Api.getAssignedUserCount(deleteTarget!.role_code),
    enabled: !!deleteTarget,
    staleTime: 0,
  });
  const deleteCount = deleteCountQuery.data ?? deleteTarget?.assigned_user_count ?? 0;
  const deleteBlocked = deleteCount > 0;

  if (rolesQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: colors.adminAccent }} />
      </Box>
    );
  }
  if (rolesQuery.isError) {
    return <Alert severity="error" sx={{ borderRadius: '8px' }}>Failed to load roles — try again.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
        <Typography sx={{ ...typo.secondaryText }}>
          Roles are personas that bundle permissions; assign one to each user on the Users tab.
          System roles are locked.
        </Typography>
        <WriteButton ignoreEnvReadOnly variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ ...primaryButtonSx, flexShrink: 0 }}>
          New role
        </WriteButton>
      </Box>

      {roles.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: '8px' }}>No roles defined yet — create one to get started.</Alert>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
          {roles.map((r) => (
            <RolePersonaCard
              key={r.role_code}
              role={r}
              onEdit={openEdit}
              onDelete={setDeleteTarget}
              onView={openView}
            />
          ))}
        </Box>
      )}

      <RolePersonaEditorDialog
        open={editorOpen}
        mode={editorMode}
        role={activeRole}
        saving={saving}
        onClose={() => { if (!saving) { setEditorOpen(false); setActiveRole(null); } }}
        onSubmit={handleSubmit}
      />

      <Dialog open={!!deleteTarget} onClose={() => { if (!deleteMut.isPending) setDeleteTarget(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ ...typo.sectionTitle }}>{deleteBlocked ? 'Can’t delete this role' : 'Delete role?'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14, color: colors.textSecondary }}>
            {deleteTarget && (<>You’re about to delete <b>{deleteTarget.role_name}</b>.{' '}</>)}
            {deleteCountQuery.isError
              ? 'Could not check how many users currently hold this role — proceed with caution.'
              : deleteCountQuery.isFetching
                ? 'Checking assignments…'
                : deleteBlocked
                  ? `It’s currently held by ${deleteCount} user${deleteCount === 1 ? '' : 's'}. Every user must have exactly one role, so reassign ${deleteCount === 1 ? 'that user' : 'those users'} to another role first, then delete this one.`
                  : 'It isn’t held by any users. This can’t be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteMut.isPending} variant="outlined" sx={secondaryButtonSx}>
            {deleteBlocked ? 'Close' : 'Cancel'}
          </Button>
          {!deleteBlocked && (
            <Button
              onClick={() => deleteTarget && deleteMut.mutate(deleteTarget.role_code)}
              disabled={deleteMut.isPending}
              variant="contained"
              sx={{ ...primaryButtonSx, backgroundColor: colors.errorIcon, color: colors.bgWhite, '&:hover': { backgroundColor: colors.errorText } }}
            >
              {deleteMut.isPending ? 'Deleting…' : 'Delete role'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolePersonasPanel;
